/**
 * Pure keystroke classifier for MultiFactorCodeField's per-digit input boxes
 * (forgot-password confirmation code, SMS MFA login challenge). Decides
 * whether a non-Backspace keyup should accept-and-advance to the next box
 * or wipe the box, without touching the DOM.
 *
 * Contains:
 * - NAVIGATION_KEYS — keys that must never wipe or advance a box
 * - resolveKeyedCharacter — pure keyup resolver (named export)
 *
 * Returns plain JS — no JSX, no DOM access.
 */

export const NAVIGATION_KEYS = Object.freeze([
  'Shift',
  'Control',
  'Alt',
  'Meta',
  'Tab',
  'CapsLock',
  'Escape',
  'Enter',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
]);

/**
 * @param {string} key - KeyboardEvent.key from the keyup that just fired. Unreliable for
 *   composed input: mobile IME keyboards report "Unidentified" for a typed digit even
 *   though the browser already wrote the correct character to the input's value. Also
 *   unreliable after a paste: the paste handler focuses each box in turn, so the trailing
 *   keyup for the paste shortcut (e.g. Ctrl/Cmd+V's "v") retargets to whichever box now
 *   has focus and reports that key instead of a digit.
 * @param {string} currentValue - the input's value immediately after the keyup, i.e. what
 *   the browser actually wrote (or left unchanged for a non-character keyup).
 * @param {string} allowedCharacters - regex source the resolved character must match.
 * @returns {{ advance: boolean, value: string }} `value` is what the box should hold;
 *   `advance` is whether focus should move to the next box.
 */
export function resolveKeyedCharacter(key, currentValue, allowedCharacters) {
  if (NAVIGATION_KEYS.includes(key)) {
    return { advance: false, value: currentValue };
  }
  if (currentValue && currentValue.match(allowedCharacters)) {
    return { advance: true, value: currentValue };
  }
  return { advance: false, value: '' };
}
